import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Clock3,
  Newspaper,
  Sparkles,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import he from "he";
import axios from "axios";
import LogoFallback from "/img/logo.png";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_SOURCE_URL = import.meta.env.VITE_API_SOURCE_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const imageReveal = {
  hidden: { opacity: 0, scale: 1.04 },
  visible: { opacity: 1, scale: 1 },
};

export default function NewsDetails({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "News | University of Creative Technology Chittagong",
}) {
  const { id } = useParams();

  const [allnews, setAllnews] = useState([]);
  const [news, setNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentError, setRecentError] = useState(null);

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { "Content-Type": "application/json" },
  });

  const decodeTitle = (rawTitle = "") => {
    return he.decode(String(rawTitle));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDescription = (textdata = "") => {
    if (!textdata) return "";

    return String(textdata)
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\n/g, "<br />")
      .replace(/\\r/g, " ")
      .replace(/\\/g, " ");
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    return `${API_SOURCE_URL}${imagePath}`;
  };

  const handlePost = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        id,
      };

      const response = await api.post("/news-details", payload);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];

      setNews(data);
      setSelectedNews(data[0] || null);
    } catch (err) {
      setError(err.message || "Unable to load this news.");
      setNews([]);
      setSelectedNews(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const newsPost = useCallback(async () => {
    setRecentLoading(true);
    setRecentError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: "0",
      };

      const response = await api.post("/recent-news", payload);
      setAllnews(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setRecentError(err.message || "Unable to load recent news.");
      setAllnews([]);
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    handlePost();
  }, [handlePost]);

  useEffect(() => {
    newsPost();
  }, [newsPost]);

  useEffect(() => {
    document.title = selectedNews
      ? `${decodeTitle(selectedNews.head_line)} | UCTC`
      : pageTitle;
  }, [pageTitle, selectedNews]);

  const rootStyle = {
    "--accent": accent,
  };

  const handleRecentNewsClick = (item) => {
    setSelectedNews(item);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isSelected = (item) =>
    String(item?.id) === String(selectedNews?.id);

  return (
    <main
      style={rootStyle}
      className="relative min-h-screen overflow-hidden bg-[#f4fbfd] text-slate-800"
    >
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-40 h-96 w-96 rounded-full bg-cyan-200/30 blur-3xl" />
        <div className="absolute -right-40 top-[700px] h-96 w-96 rounded-full bg-emerald-200/20 blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-[#014864]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#01384e] via-[#014864] to-[#087d86]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -right-20 -top-32 h-96 w-96 rounded-full border-[45px] border-white/20" />
          <div className="absolute -bottom-48 left-10 h-96 w-96 rounded-full border-[45px] border-white/10" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 md:pb-32 md:pt-24"
        >
          <div className="mb-7 flex items-center gap-3 text-sm font-semibold text-cyan-100">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur">
              <Newspaper size={20} />
            </span>
            <span className="uppercase tracking-[0.2em]">UCTC Newsroom</span>
          </div>

          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur"
            >
              <Sparkles size={14} />
              University updates & stories
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.h1
                key={selectedNews?.id || "loading"}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                className="text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl"
              >
                {selectedNews
                  ? decodeTitle(selectedNews.head_line)
                  : loading
                    ? "Discover the latest from UCTC"
                    : "News & Updates"}
              </motion.h1>
            </AnimatePresence>

            <p className="mt-6 max-w-2xl text-base leading-7 text-cyan-50/80 md:text-lg">
              Discover the latest announcements, achievements, events, and
              stories from the University of Creative Technology Chittagong.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-cyan-50/80">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 font-medium transition hover:bg-white/20"
              >
                <ArrowLeft size={16} />
                Back to Home
              </Link>

              <span className="hidden h-5 w-px bg-white/20 sm:block" />

              <span className="inline-flex items-center gap-2">
                <CalendarDays size={16} />
                {selectedNews?.date
                  ? formatDate(selectedNews.date)
                  : "Latest university news"}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 100"
            xmlns="http://www.w3.org/2000/svg"
            className="h-14 w-full md:h-20"
            preserveAspectRatio="none"
          >
            <path
              d="M0,55 C220,100 450,5 720,50 C1000,95 1200,15 1440,55 L1440,100 L0,100 Z"
              fill="#f4fbfd"
            />
          </svg>
        </div>
      </section>

      {/* Main content */}
      <section className="relative z-10 mx-auto max-w-7xl px-5 pb-20 pt-8 sm:px-8 md:pt-12">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3 lg:gap-10">
          {/* News article */}
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="overflow-hidden rounded-3xl border border-white bg-white p-5 shadow-xl shadow-cyan-900/5 sm:p-8"
                >
                  <div className="mb-6 h-8 w-3/4 animate-pulse rounded-lg bg-slate-200" />
                  <div className="mb-8 h-64 animate-pulse rounded-2xl bg-slate-100 sm:h-96" />
                  <div className="space-y-3">
                    <div className="h-4 animate-pulse rounded bg-slate-100" />
                    <div className="h-4 w-11/12 animate-pulse rounded bg-slate-100" />
                    <div className="h-4 w-9/12 animate-pulse rounded bg-slate-100" />
                  </div>
                </motion.div>
              ) : error ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl border border-red-100 bg-white p-8 text-center shadow-xl shadow-red-900/5"
                >
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                    <Newspaper size={25} />
                  </div>
                  <h2 className="text-xl font-bold text-slate-800">
                    Unable to load this news
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">{error}</p>
                  <button
                    type="button"
                    onClick={handlePost}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#014864] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#02627d]"
                  >
                    <RefreshCw size={16} />
                    Try Again
                  </button>
                </motion.div>
              ) : selectedNews ? (
                <motion.article
                  key={selectedNews.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45 }}
                  className="overflow-hidden rounded-3xl border border-white bg-white shadow-xl shadow-cyan-900/5"
                >
                  {/* Article heading */}
                  <div className="px-5 pb-5 pt-7 sm:px-8 sm:pt-9">
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#014864]">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        University News
                      </span>

                      {selectedNews.date && (
                        <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                          <CalendarDays size={15} />
                          {formatDate(selectedNews.date)}
                        </span>
                      )}
                    </div>

                    <h2 className="text-2xl font-extrabold leading-snug text-[#014864] sm:text-3xl">
                      {decodeTitle(selectedNews.head_line)}
                    </h2>
                  </div>

                  {/* Feature image */}
                  {selectedNews.images_path && (
                    <motion.div
                      variants={imageReveal}
                      initial="hidden"
                      animate="visible"
                      transition={{ duration: 0.7 }}
                      className="group relative mx-5 overflow-hidden rounded-2xl sm:mx-8"
                    >
                      <img
                        src={getImageUrl(selectedNews.images_path)}
                        alt={decodeTitle(selectedNews.head_line)}
                        className="max-h-[520px] min-h-[220px] w-full object-cover transition duration-700 group-hover:scale-[1.025]"
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#01384e]/20 via-transparent to-transparent" />
                    </motion.div>
                  )}

                  {/* Article body */}
                  <div className="px-5 pb-8 pt-7 sm:px-8 sm:pb-10">
                    <div
                      className="news-content max-w-none break-words text-[15px] leading-8 text-slate-600 sm:text-base"
                      dangerouslySetInnerHTML={{
                        __html: formatDescription(selectedNews.description),
                      }}
                    />
                  </div>

                  {/* Article footer */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 bg-slate-50/80 px-5 py-5 sm:px-8">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Clock3 size={16} />
                      <span>University of Creative Technology Chittagong</span>
                    </div>

                    <Link
                      to="/news"
                      className="inline-flex items-center gap-2 text-sm font-bold text-[#014864] transition hover:gap-3"
                    >
                      All News
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.article>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-3xl border border-white bg-white p-10 text-center shadow-xl shadow-cyan-900/5"
                >
                  <Newspaper className="mx-auto mb-4 text-slate-300" size={42} />
                  <h2 className="text-xl font-bold text-slate-700">
                    No news found
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    The requested news article may have been removed or is
                    currently unavailable.
                  </p>
                  <Link
                    to="/news"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#014864] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#02627d]"
                  >
                    Browse All News
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Recent news sidebar */}
          <aside className="lg:sticky lg:top-6">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden rounded-3xl border border-white bg-white shadow-xl shadow-cyan-900/5"
            >
              <div className="relative overflow-hidden bg-[#014864] px-6 py-6">
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full border-[18px] border-white/10" />
                <div className="relative flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-100">
                      Stay informed
                    </p>
                    <h3 className="mt-2 text-2xl font-extrabold text-white">
                      Recent News
                    </h3>
                  </div>
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-white">
                    <Newspaper size={21} />
                  </span>
                </div>
              </div>

              <div className="p-4 sm:p-5">
                {recentLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((item) => (
                      <div
                        key={item}
                        className="flex animate-pulse gap-3 rounded-2xl p-2"
                      >
                        <div className="h-20 w-24 shrink-0 rounded-xl bg-slate-100" />
                        <div className="flex-1 space-y-2 py-2">
                          <div className="h-3 rounded bg-slate-100" />
                          <div className="h-3 w-2/3 rounded bg-slate-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : recentError ? (
                  <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-600">
                    {recentError}
                    <button
                      type="button"
                      onClick={newsPost}
                      className="mt-3 inline-flex items-center gap-2 font-bold"
                    >
                      <RefreshCw size={14} />
                      Retry
                    </button>
                  </div>
                ) : allnews.length > 0 ? (
                  <div className="space-y-2">
                    {allnews
                      .filter((item) => String(item.id) !== String(id))
                      .slice(0, 8)
                      .map((item, index) => (
                        <motion.button
                          type="button"
                          key={item.id}
                          initial={{ opacity: 0, x: 18 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.35,
                            delay: Math.min(index * 0.06, 0.3),
                          }}
                          onClick={() => handleRecentNewsClick(item)}
                          className={`group flex w-full items-center gap-3 rounded-2xl border p-2.5 text-left transition duration-300 ${
                            isSelected(item)
                              ? "border-cyan-100 bg-cyan-50"
                              : "border-transparent hover:border-slate-100 hover:bg-slate-50"
                          }`}
                        >
                          <div className="relative h-[76px] w-[88px] shrink-0 overflow-hidden rounded-xl bg-slate-100">
                            {item.images_path ? (
                              <img
                                src={getImageUrl(item.images_path)}
                                alt={decodeTitle(item.head_line)}
                                loading="lazy"
                                className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-300">
                                <Newspaper size={24} />
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            {item.date && (
                              <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                {formatDate(item.date)}
                              </span>
                            )}
                            <h4
                              className={`line-clamp-2 text-sm font-bold leading-5 transition ${
                                isSelected(item)
                                  ? "text-[#014864]"
                                  : "text-slate-700 group-hover:text-[#014864]"
                              }`}
                            >
                              {decodeTitle(item.head_line)}
                            </h4>
                          </div>

                          <ChevronRight
                            size={16}
                            className="shrink-0 text-slate-300 transition group-hover:translate-x-1 group-hover:text-[#014864]"
                          />
                        </motion.button>
                      ))}
                  </div>
                ) : (
                  <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500">
                    No recent news available.
                  </p>
                )}

                <Link
                  to="/news"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm font-bold text-[#014864] transition hover:bg-[#014864] hover:text-white"
                >
                  View All News
                  <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          </aside>
        </div>
      </section>

      {/* Small scoped styles for rich article content */}
      <style>{`
        .news-content p {
          margin: 0 0 1.25rem;
        }

        .news-content h1,
        .news-content h2,
        .news-content h3,
        .news-content h4 {
          color: #014864;
          font-weight: 800;
          line-height: 1.4;
          margin: 1.75rem 0 0.9rem;
        }

        .news-content ul,
        .news-content ol {
          margin: 1rem 0 1.5rem;
          padding-left: 1.5rem;
        }

        .news-content ul {
          list-style: disc;
        }

        .news-content ol {
          list-style: decimal;
        }

        .news-content li {
          margin: 0.4rem 0;
        }

        .news-content a {
          color: #087d86;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .news-content img {
          max-width: 100%;
          height: auto;
          border-radius: 1rem;
          margin: 1.5rem auto;
        }

        .news-content blockquote {
          margin: 1.5rem 0;
          border-left: 4px solid #0e7490;
          padding: 0.75rem 1.25rem;
          background: #f0f9ff;
          border-radius: 0 0.75rem 0.75rem 0;
          color: #475569;
        }

        .news-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }

        .news-content th,
        .news-content td {
          border: 1px solid #e2e8f0;
          padding: 0.65rem;
          text-align: left;
        }

        .news-content th {
          background: #f0f9ff;
          color: #014864;
        }
      `}</style>
    </main>
  );
}

NewsDetails.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};